import { HttpClient, robotoHeaders } from "@/shared/http";
import { PaginatedResponse } from "@/shared/http/response";

import {
  BeginWebhookIntegrationRequest,
  WebhookIntegrationRecord,
} from "./WebhookIntegrationRecord";

interface Options {
  abortSignal?: AbortSignal;
}

export class IntegrationService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async beginWebhookIntegration(
    request: BeginWebhookIntegrationRequest,
    options?: Partial<Options>,
  ): Promise<WebhookIntegrationRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      "v1/integrations/webhook/begin",
    );
    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({
        orgId: request.orgId,
      }),
      body: JSON.stringify({
        org_id: request.orgId,
        user_config: request.userConfig,
        webhook_provider: request.webhookProvider,
        webhook_events: request.webhookEvents,
      }),
    });
    return response.json<WebhookIntegrationRecord>();
  }

  public async getWebhooksIntegrationByProvider(
    provider: string,
    orgId: string,
    options?: Partial<Options>,
  ): Promise<PaginatedResponse<WebhookIntegrationRecord>> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/integrations/webhook/provider/${provider}`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({
        orgId: orgId,
      }),
    });
    return response.json<PaginatedResponse<WebhookIntegrationRecord>>();
  }

  public async updateWebhookIntegrationSecret(
    webhookEndpointId: string,
    webhookSecret: string,
    options?: Partial<Options>,
  ): Promise<WebhookIntegrationRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/integrations/webhook/id/${webhookEndpointId}/signature_secret`,
    );
    const response = await this.#httpClient.put(requestUrl, {
      signal: options?.abortSignal,
      body: JSON.stringify({
        webhook_secret: webhookSecret,
      }),
    });
    return response.json<WebhookIntegrationRecord>();
  }

  public async getWebhookIntegration(
    webhookEndpointId: string,
    options?: Partial<Options>,
  ): Promise<WebhookIntegrationRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/integrations/webhook/id/${webhookEndpointId}`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });
    return response.json<WebhookIntegrationRecord>();
  }

  public async updateWebhookIntegrationApiKey(
    webhookEndpointId: string,
    apiKey: string,
    options?: Partial<Options>,
  ): Promise<WebhookIntegrationRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/integrations/webhook/id/${webhookEndpointId}/api_key`,
    );
    const response = await this.#httpClient.put(requestUrl, {
      signal: options?.abortSignal,
      body: JSON.stringify({
        webhook_provider_api_key: apiKey,
      }),
    });
    return response.json<WebhookIntegrationRecord>();
  }

  public async deleteWebhookIntegration(
    webhookEndpointId: string,
    options?: Partial<Options>,
  ): Promise<void> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/integrations/webhook/id/${webhookEndpointId}`,
    );
    const response = await this.#httpClient.delete(requestUrl, {
      signal: options?.abortSignal,
    });

    return response.throwIfError();
  }
}
